/**
 * translationSpanEdit.ts — PLAT-10214 (T6, epic PLAT-10209)
 *
 * Pure span helpers for the reversible translated-span replace. A reviewer
 * highlights a contiguous run of TRANSLATED words and types one replacement;
 * the workspace (and later the inline pill, T12) turns that into a reversible
 * **manual-override segment** (epic D10) over the covered translation
 * fragments — it does NOT destroy them.
 *
 * No React, no MobX, no store imports — a deterministic projection over plain
 * `TranslationPhrase` / `ParentPhrase` data so it unit-tests in isolation. The
 * store ops (`createOverrideSegment` / `updateOverrideSegment` /
 * `revertOverrideSegment`) compose these helpers; the selection→phrases
 * resolution lives in `resolveTranslatedSelectionPhrases.ts`.
 *
 * Absorbs the never-committed PLAT-10210 draft's pure helpers (union / lead /
 * order / join / display-model). The one behavioral change from that draft: the
 * destructive `mergeCoveredPhrases` (which collapsed covered phrases into one,
 * losing the original tiling) is GONE — replaced by `buildOverrideSegment`,
 * which produces the override segment record + the list of originals to RETIRE
 * (mark `active: false`) so undo restores the exact machine tiling (D10).
 */

import { joinPhraseValues } from '@src/utility/strings';

import type {
  ParentPhrase,
  TranslationPhrase,
} from '@src/types.ts/translation';

/** Where a manual-authored segment came from. `manual` = hand-typed override
 *  (D10); `directed` = machine-steered re-translation (D13, T14). Both reuse
 *  `buildOverrideSegment` / the store's `createOverrideSegment`. */
export type OverrideOrigin = 'manual' | 'directed';

/**
 * Segment-ordering key for a phrase: its smallest source `initial_index`
 * (= the value the translated DOM stamps on `data-initial-index`, and the
 * anchor the override-segment id is built from). `Number.MAX_SAFE_INTEGER`
 * for an index-less phrase so it sorts last.
 */
export function phraseLead(phrase: TranslationPhrase): number {
  if (phrase.originalIndices.length === 0) return Number.MAX_SAFE_INTEGER;
  let lead = phrase.originalIndices[0];
  for (const idx of phrase.originalIndices) if (idx < lead) lead = idx;
  return lead;
}

/**
 * Sorted (ascending), de-duped union of every covered phrase's
 * `originalIndices`. The override segment's source span. Defensive sort +
 * dedupe: a phrase's own `originalIndices` are not guaranteed sorted, and two
 * phrases never legitimately share a source index in the ACTIVE set, but a
 * caller passing overlap shouldn't produce a duplicated index.
 */
export function unionOriginalIndices(
  phrases: readonly TranslationPhrase[],
): number[] {
  const set = new Set<number>();
  for (const p of phrases) {
    for (const idx of p.originalIndices) set.add(idx);
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * PLAT-10611 (F5) — de-duped union of every covered phrase's
 * `originalElementIds` (content-only stable UUIDs), in the phrases' given order
 * (the caller passes them lead-ordered, so this is source order). Drives the
 * id-first original-pane read (R13) and the store's id-first covered-set
 * intersection. Empty when no covered phrase carries ids (legacy blob).
 */
export function unionOriginalElementIds(
  phrases: readonly TranslationPhrase[],
): string[] {
  const set = new Set<string>();
  for (const p of phrases) {
    if (p.originalElementIds)
      for (const id of p.originalElementIds) set.add(id);
  }
  return [...set];
}

/**
 * Covered phrases in source order (ascending by lead). Returns a NEW array;
 * does not mutate the input. The join + snapshot helpers below rely on this
 * ordering so the reconstructed text reads in document order regardless of the
 * order the selection resolver produced.
 */
export function orderPhrasesByLead(
  phrases: readonly TranslationPhrase[],
): TranslationPhrase[] {
  return [...phrases].sort((a, b) => phraseLead(a) - phraseLead(b));
}

/**
 * Join a set of fragments' values into one string, in source order. Used to
 * reconstruct the pre-override translated text for the segment's
 * `translatedValue` fallback and the "what it used to be" display. Trims each
 * fragment (the splitter peels boundary whitespace into its own fragments) and
 * assembles them with the transcript's punctuation-aware spacing rule
 * (`joinPhraseValues` — no space before attaching/closing punctuation), so the
 * reconstructed text matches exactly what the live transcript renders.
 *
 * `pick` selects which string to read off each fragment (default
 * `translatedValue`); callers pass `originalValueSnapshot` to join the source
 * side instead.
 */
export function joinFragments(
  phrases: readonly TranslationPhrase[],
  pick: (p: TranslationPhrase) => string | undefined = p => p.translatedValue,
): string {
  return joinPhraseValues(
    orderPhrasesByLead(phrases)
      .map(pick)
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map(v => v.trim()),
  );
}

/**
 * The display model the `ReplaceSpanEditor` pre-fills from a selection: the
 * current translated text of the covered run (joined in source order), the
 * source text snapshot it covers, and the union source indices. Pure — the
 * editor renders it; the store op consumes the same `coveredIndices` on
 * confirm.
 */
export interface SpanDisplayModel {
  /** Covered phrases in source order. */
  orderedPhrases: TranslationPhrase[];
  /** The current translated text of the span (join of the covered values). */
  currentTranslatedValue: string;
  /** The source-language text the span covers (join of the snapshots). */
  originalSnapshot: string;
  /** Union of the covered source indices, ascending. */
  coveredIndices: number[];
  /**
   * PLAT-10611 (F5) — union of the covered phrases' stable element UUIDs
   * (content-only, source order). The id-first key for the original-pane read
   * (drift-correct) and the store's covered-set intersection; falls back to
   * `coveredIndices` when empty (legacy blob).
   */
  coveredElementIds: string[];
  /**
   * The single manual/directed segment this span already sits inside, if the
   * covered phrases all share one override `parentId`. Drives the D10 re-edit
   * path: when set, confirm UPDATES that segment rather than creating a new
   * one. Undefined when the span covers LLM/SRT fragments (or spans more than
   * one segment) — then confirm creates a fresh override segment.
   */
  existingOverrideSegmentId?: string;
}

/**
 * Build the pre-fill model for the editor from the covered active phrases.
 * `segmentsById` lets the helper recognize when the whole span already sits
 * inside ONE manual/directed override segment (the D10 re-edit case): when
 * every covered phrase shares a single `parentId` whose `ParentPhrase.origin`
 * is `manual`/`directed`, that id is surfaced so the caller routes confirm to
 * `updateOverrideSegment`. Pass an empty map when the caller has no segment
 * records (then it never reports an existing override).
 */
export function buildSpanDisplayModel(
  covered: readonly TranslationPhrase[],
  segmentsById: ReadonlyMap<string, ParentPhrase> = new Map(),
): SpanDisplayModel {
  const orderedPhrases = orderPhrasesByLead(covered);
  const existingOverrideSegmentId = resolveSharedOverrideSegmentId(
    orderedPhrases,
    segmentsById,
  );
  // Re-editing an existing override: the segment's stored `translatedValue` is
  // authoritative — pre-fill from it (byte-exact, including the backend's ''
  // join spacing and CJK / punctuation-tight targets) rather than re-joining the
  // derived fragments, whose join spacing need not match how the value was
  // stored (a PLAT-10269 split peels whitespace into its own tiles). A fresh,
  // non-override span has no segment, so it joins the machine fragments.
  const overrideSegment = existingOverrideSegmentId
    ? segmentsById.get(existingOverrideSegmentId)
    : undefined;
  return {
    orderedPhrases,
    currentTranslatedValue:
      overrideSegment?.translatedValue ?? joinFragments(orderedPhrases),
    originalSnapshot: joinFragments(
      orderedPhrases,
      p => p.originalValueSnapshot,
    ),
    coveredIndices: unionOriginalIndices(orderedPhrases),
    coveredElementIds: unionOriginalElementIds(orderedPhrases),
    existingOverrideSegmentId,
  };
}

/**
 * When every covered phrase carries the SAME `parentId` and that parent is a
 * `manual`/`directed` override segment, return its id (the re-edit target).
 * Otherwise undefined: the span covers no override, covers more than one
 * segment, or covers an LLM/SRT segment (whose re-edit is a fresh override, not
 * an update — D10 only treats existing MANUAL/DIRECTED segments as in-place
 * updatable).
 */
function resolveSharedOverrideSegmentId(
  covered: readonly TranslationPhrase[],
  segmentsById: ReadonlyMap<string, ParentPhrase>,
): string | undefined {
  // Derived from the plural lookup so the "what counts as an override segment"
  // rule lives in exactly one place (PLAT-11475). The span re-edits ONE
  // override iff it touches exactly one AND every covered fragment belongs to
  // it — a fragment outside it means the span extends past the override
  // (partial overlap), which is a conflict, not a re-edit.
  const touched = overrideSegmentIdsTouchedBy(covered, segmentsById);
  if (touched.length !== 1) return undefined;
  const only = touched[0];
  return covered.every(p => p.parentId === only) ? only : undefined;
}

/**
 * EVERY distinct override segment a span touches, in span order — the plural of
 * `resolveSharedOverrideSegmentId` (PLAT-11475). The singular forms answer
 * `undefined` exactly when the span sits across MORE than one correction, which
 * is the case that produces the overlap refusal, so the blocked editor's Undo
 * (which must remove every correction the selection covers) cannot use them.
 * `spanTouchesOverride` and `resolveSharedOverrideSegmentId` both derive from
 * this, so the id-scheme rule (`o_` prefix authoritative, `origin` a fallback
 * for legacy/back-end-written blobs) is stated once.
 */
export function overrideSegmentIdsTouchedBy(
  covered: readonly TranslationPhrase[],
  segmentsById: ReadonlyMap<string, ParentPhrase>,
): string[] {
  const ids: string[] = [];
  for (const p of covered) {
    const parent = p.parentId ? segmentsById.get(p.parentId) : undefined;
    if (!parent || !isOverrideParent(parent)) continue;
    if (!ids.includes(parent.id)) ids.push(parent.id);
  }
  return ids;
}

/**
 * Resolve the override segment id owning a span identified only by its source
 * words' stable ids (`originalElementIds`, no DOM selection) — the shape check
 * the coverage-popover Undo needs (PLAT-10268, stable-id migration). Gathers the
 * ACTIVE phrases covering `coveredElementIds` by STABLE-ID overlap and delegates
 * to the SAME `resolveSharedOverrideSegmentId` the span editor uses (via
 * `buildSpanDisplayModel`), so the `o_`-prefix id-scheme logic lives in one
 * place. Returns the segment id when the covered span sits entirely inside one
 * manual/directed override (route Undo to `revertOverrideSegment`), or undefined
 * for a non-override span (route Undo to per-phrase `revertTranslation`). No
 * positional-index path — a phrase with no ids is a stale translation and never
 * overlaps.
 */
export function resolveOverrideSegmentIdForElementIds(
  phrases: readonly TranslationPhrase[],
  parentPhrases: readonly ParentPhrase[],
  coveredElementIds: readonly string[],
): string | undefined {
  const covered = new Set(coveredElementIds);
  const coveredPhrases = phrases.filter(
    p =>
      p.active !== false &&
      (p.originalElementIds?.some(id => covered.has(id)) ?? false),
  );
  if (coveredPhrases.length === 0) return undefined;
  const segmentsById = new Map<string, ParentPhrase>();
  for (const seg of parentPhrases) segmentsById.set(seg.id, seg);
  return buildSpanDisplayModel(coveredPhrases, segmentsById)
    .existingOverrideSegmentId;
}

/**
 * Does a span TOUCH any existing override segment? True when ANY covered
 * phrase belongs to a manual/directed override parent (the `o_` id scheme is
 * authoritative; `origin` may be absent on a legacy/back-end-written blob —
 * same test as `resolveSharedOverrideSegmentId` above). This is the SMALL
 * extraction of the overlap predicate `useSpanReplace.resolvePending` used
 * inline (PLAT-10257 A2): the directed re-translate path refuses ANY touching
 * span — within / exact-match / extending / spans-two (locked decision
 * 2026-07-16) — and the coverage-popover directed entry (A4) reuses the same
 * predicate. The predicate only DETECTS the touch; the caller decides what it
 * means (the manual path still allows the clean within-one-override re-edit
 * via `existingOverrideSegmentId`).
 */
export function spanTouchesOverride(
  covered: readonly TranslationPhrase[],
  segmentsById: ReadonlyMap<string, ParentPhrase>,
): boolean {
  return overrideSegmentIdsTouchedBy(covered, segmentsById).length > 0;
}

/**
 * The override-segment id for a span anchored at `anchor`. `"o_<anchor>"` — a
 * distinct scheme from the LLM/SRT `"p_<anchor>"` so a manual/directed override
 * never collides with a first-pass segment over the same anchor (epic D10 /
 * Data Model).
 *
 * PLAT-10611 (F4): the anchor is now the covering element's STABLE id
 * (`originalElementIds[0]`), a string, so the override identity survives a
 * reordered / drifted clip. `overrideSegmentId` accepts a `string | number` so
 * legacy id-less spans still mint `o_<integer>` (the id-string only — the
 * MATCH/detect paths key on the opaque `o_` prefix, never parse the anchor
 * back to an int, so a legacy override is still dropped, not integer-matched,
 * on the identity path).
 */
export function overrideSegmentId(anchor: string | number): string {
  return `o_${anchor}`;
}

/** The result of building an override over a set of covered active fragments. */
export interface BuiltOverrideSegment {
  /** The new override segment record to insert into `parentPhrases[]`. */
  segment: ParentPhrase;
  /**
   * The covered fragments, flagged RETIRED (`active: false`, `supersededBy` →
   * the new segment id) — to REPLACE the originals in `phrases[]`, NOT delete
   * them. Retains each fragment's own `parentId` so its relation to the segment
   * it used to belong to survives (D10). This is what makes the override
   * reversible: undo flips these back to active + clears `supersededBy`.
   */
  retired: TranslationPhrase[];
}

/**
 * Build the reversible manual-override segment for a span (epic D10, step 2 +
 * step 1). Pure: produces the segment record and the retired-originals list;
 * the store op inserts the segment, swaps the originals for these retired
 * copies, inserts the override's own fragment(s), and saves.
 *
 * - `segment.id` = `"o_<min covered index>"` (distinct from LLM/SRT `p_…`).
 * - `segment.originalIndices` = sorted union of the covered fragments' indices
 *   (the contiguous source span the override owns).
 * - `segment.translatedValue` = the reviewer's full override text (authoritative
 *   text; its fragments are always a derived split of it — D10 re-edit).
 * - `segment.originalValueSnapshot` = the covered source text (join of the
 *   covered fragments' snapshots) so coverage/staleness still compare against
 *   the live source words.
 * - `segment.origin` = `'manual'` (default) or `'directed'` (T14).
 * - `retired` = each covered fragment with `active: false` + `supersededBy` set
 *   to `segment.id`, `parentId` preserved.
 *
 * Throws on an empty `covered` (an override with no source span is meaningless)
 * — the caller (the store op) gates the multi-fragment path on `covered.length`.
 */
export function buildOverrideSegment(
  covered: readonly TranslationPhrase[],
  editedValue: string,
  origin: OverrideOrigin = 'manual',
): BuiltOverrideSegment {
  if (covered.length === 0) {
    throw new Error(
      'buildOverrideSegment: no covered fragments — cannot author an override over an empty span',
    );
  }
  const ordered = orderPhrasesByLead(covered);
  const originalIndices = unionOriginalIndices(ordered);
  const anchorIndex = originalIndices[0];
  // PLAT-10611 (F4) — mint the override identity from the covering element's
  // STABLE id (the first covered phrase's first `originalElementIds`), so the
  // override survives a reordered / drifted clip. Fall back to the integer
  // anchor for the id STRING only when the covered phrases carry no element ids
  // (legacy blob) — the covered set + `originalIndices` (ordinal) stay integer,
  // and the `o_` prefix readers are opaque, so this is a mint-string fallback,
  // NOT an identity-match fallback (a legacy override is still dropped on the
  // id-only identity path, never integer-matched to the wrong words).
  const anchorElementId = ordered[0].originalElementIds?.[0];
  const id = overrideSegmentId(anchorElementId ?? anchorIndex);
  const originalValueSnapshot = joinFragments(
    ordered,
    p => p.originalValueSnapshot,
  );

  const segment: ParentPhrase = {
    id,
    originalIndices,
    originalElementIds: unionOriginalElementIds(ordered),
    translatedValue: editedValue,
    originalValueSnapshot,
    origin,
  };

  const retired: TranslationPhrase[] = ordered.map(p => ({
    ...p,
    active: false,
    supersededBy: id,
  }));

  return { segment, retired };
}

// --- PLAT-10258 — whole-translation directed re-translate (D13) ------------

/**
 * One reviewer fix collected off the ACTIVE translation state (PLAT-10258).
 * `originalIndices` is the fix's INTEGER source anchor (`initial_index` values);
 * PLAT-10611 demoted these to request-scoped ephemeral positions, so they are
 * only a safe re-anchor when the transcript is unchanged between fix-capture and
 * re-translate. `originalElementIds` is the fix's DURABLE anchor (PLAT-10611's
 * stable content UUIDs) — present whenever the source phrase carried ids, absent
 * on legacy/pre-UUID blobs — and is what the keep-fixes re-apply anchors on so a
 * kept fix survives a position shift. `translatedValue` is the reviewer's text
 * to re-apply (and to show when the fix cannot re-attach). `manual`/`directed` =
 * an override SEGMENT fix (counted once, off its `ParentPhrase`); `single-edit`
 * = a standalone user-edited phrase.
 */
export interface CollectedUserFix {
  originalIndices: number[];
  /** PLAT-10611 durable identity of the fixed source words; undefined on a
   *  legacy/id-less fix, which then re-anchors by `originalIndices` (integer). */
  originalElementIds?: string[];
  translatedValue: string;
  origin: OverrideOrigin | 'single-edit';
}

/**
 * An override parent — the `o_` id scheme is authoritative; `origin` may be
 * absent on a legacy/back-end blob (same test as
 * `resolveSharedOverrideSegmentId` above and the store's overlap guard).
 */
function isOverrideParent(parent: ParentPhrase): boolean {
  return (
    parent.id.startsWith('o_') ||
    parent.origin === 'manual' ||
    parent.origin === 'directed'
  );
}

/** Smallest source index of a fix's span (cf. `phraseLead`); index-less last. */
function leadIndex(indices: readonly number[]): number {
  if (indices.length === 0) return Number.MAX_SAFE_INTEGER;
  let lead = indices[0];
  for (const idx of indices) if (idx < lead) lead = idx;
  return lead;
}

/**
 * Collect the reviewer's fixes on a translation — ONE entry per live override
 * segment plus one per standalone user-edited phrase (PLAT-10258). The single
 * source of truth for BOTH the Fresh-rebase discard count ("N fixes will be
 * lost") and the Keep-my-fixes snapshot re-applied after the rebase.
 *
 * - An override segment counts ONCE, off its `ParentPhrase` record
 *   (`translatedValue` + `originalIndices`) — never off its derived fragments,
 *   which also carry `isUserEdited: true` (the double-count trap). A segment
 *   only counts while LIVE: at least one ACTIVE fragment references it (a
 *   segment whose fragments were all retired is not a current fix).
 * - A standalone `isUserEdited` phrase (active, not derived from an override
 *   segment — in-place edits under LLM/SRT `p_` parents included) counts as a
 *   `single-edit` fix.
 * - Everything filters `active !== false`; retired fragments never count.
 *
 * Ordering contract: override-segment fixes FIRST, then single edits, each
 * ascending by lead source index. This is the re-application order —
 * `directedRetranslateWhole` replays the list as-is, and a segment fix must
 * win when the rebased tiling merges a segment and a single edit into one
 * phrase.
 *
 * Pure — no store access; a deterministic projection over plain data.
 */
export function collectUserFixes(
  phrases: readonly TranslationPhrase[],
  parentPhrases: readonly ParentPhrase[],
): CollectedUserFix[] {
  // Parents referenced by at least one ACTIVE fragment — the live segments.
  const liveParentIds = new Set<string>();
  for (const p of phrases) {
    if (p.active !== false && p.parentId !== undefined) {
      liveParentIds.add(p.parentId);
    }
  }

  const overrideParentIds = new Set<string>();
  const segmentFixes: CollectedUserFix[] = [];
  for (const parent of parentPhrases) {
    if (!isOverrideParent(parent)) continue;
    // Track EVERY override parent id (live or not) so the standalone pass
    // below never mistakes a derived override fragment for a single edit.
    overrideParentIds.add(parent.id);
    if (!liveParentIds.has(parent.id)) continue;
    segmentFixes.push({
      originalIndices: [...parent.originalIndices],
      // PLAT-10611 durable anchor — copied when the parent carries stable ids;
      // left undefined on a legacy/id-less segment (integer re-anchor fallback).
      originalElementIds: parent.originalElementIds
        ? [...parent.originalElementIds]
        : undefined,
      translatedValue: parent.translatedValue,
      origin: parent.origin === 'directed' ? 'directed' : 'manual',
    });
  }

  const singleEditFixes: CollectedUserFix[] = [];
  for (const p of phrases) {
    if (p.active === false || !p.isUserEdited) continue;
    // A derived override fragment — its segment above already counted it.
    if (p.parentId !== undefined && overrideParentIds.has(p.parentId)) {
      continue;
    }
    singleEditFixes.push({
      originalIndices: [...p.originalIndices],
      // PLAT-10611 durable anchor — copied when the phrase carries stable ids;
      // left undefined on a legacy/id-less phrase (integer re-anchor fallback).
      originalElementIds: p.originalElementIds
        ? [...p.originalElementIds]
        : undefined,
      translatedValue: p.translatedValue,
      origin: 'single-edit',
    });
  }

  const byLead = (a: CollectedUserFix, b: CollectedUserFix) =>
    leadIndex(a.originalIndices) - leadIndex(b.originalIndices);
  return [...segmentFixes.sort(byLead), ...singleEditFixes.sort(byLead)];
}

/**
 * PLAT-10270 (criterion 8) — has the reviewer made at least one TYPED manual
 * correction on this translation? This gates the global "Split into Words"
 * button: the whole-translation fine-split is only offered once there is a
 * hand-typed fix to preserve.
 *
 * A TYPED fix = a `manual` override segment OR a standalone `single-edit`
 * phrase. It EXCLUDES `directed` (machine-steered re-translation, not a typed
 * correction). Derived from the SAME classifier as `collectUserFixes`, which
 * already resolves the two traps that a naive check falls into:
 *   - `isUserEdited` ALONE is wrong: a directed override's derived fragments
 *     also carry `isUserEdited: true`, so it would wrongly enable after a
 *     directed-only re-translate.
 *   - `origin` ALONE is wrong: a single-phrase in-place edit writes no
 *     `origin: 'manual'` segment.
 * Pure — a deterministic projection over plain data (same contract as
 * `collectUserFixes`); evaluated per language on that language's phrases.
 */
export function hasTypedManualFix(
  phrases: readonly TranslationPhrase[],
  parentPhrases: readonly ParentPhrase[],
): boolean {
  return collectUserFixes(phrases, parentPhrases).some(
    f => f.origin === 'manual' || f.origin === 'single-edit',
  );
}
